import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col } from "@amzn/stencil-react-components/layout";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import Input from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { getString } from "common/uistringlabels/uiStringUtils";
import { Visibility } from "lib/models/list-models";
import React, { FunctionComponent, useState } from "react";

import { useLists } from "../../../view/Page/Lists/useLists";

interface ListModalProps {
  onClose: () => void;
  initialData?: { name?: string; description?: string; visibility?: Visibility };
  taxonomyId?: string;
  isEdit?: boolean;
}

const ListModal: FunctionComponent<ListModalProps> = ({ onClose, initialData, taxonomyId, isEdit = false }) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [visibility, setVisibility] = useState<Visibility>(initialData?.visibility || Visibility.PRIVATE);

  const { createList, updateList } = useLists();

  const handleSubmit = () => {
    const data = { name, description, visibility };
    if (isEdit && taxonomyId) {
      updateList(taxonomyId, data);
    } else {
      createList(data);
    }
    onClose();
  };

  const isValid = name.trim().length > 0;

  return (
    <ModalContent
      titleText={getString(isEdit ? "lists.modal.editTitle" : "lists.modal.title")}
      maxWidth="800px"
      buttons={[
        <Button key="cancel" variant={ButtonVariant.Tertiary} onClick={onClose}>
          {getString("lists.modal.cancel")}
        </Button>,
        <Button key="submit" variant={ButtonVariant.Primary} disabled={!isValid} onClick={handleSubmit}>
          {getString(isEdit ? "lists.modal.update" : "lists.modal.create")}
        </Button>,
      ]}
    >
      <Col gridGap="S300" width={"500px"}>
        <InputWrapper id="list-name" labelText={getString("lists.modal.name")} required={true}>
          {(inputProps) => (
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={getString("lists.modal.namePlaceholder")}
              touchpoint="list-name-input"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-description" labelText={getString("lists.modal.description")}>
          {(inputProps) => (
            <TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={getString("lists.modal.descriptionPlaceholder")}
              rows={3}
              touchpoint="list-description-textarea"
              {...inputProps}
            />
          )}
        </InputWrapper>

        <InputWrapper id="list-visibility" labelText={getString("lists.modal.visibility")}>
          {(inputProps) => (
            <Select
              value={visibility}
              onChange={(value) => setVisibility(value as Visibility)}
              options={["private", "shared", "public"]}
              {...inputProps}
            />
          )}
        </InputWrapper>
      </Col>
    </ModalContent>
  );
};

export default ListModal;
